'use client';

import { useEffect, useState, type ReactNode } from 'react';
import { Breadcrumb } from '@/components/ui/Breadcrumb';
import { ProductGallery } from '@/components/ui/ProductGallery';
import { ProductInfo } from '@/components/ui/ProductInfo';
import { TechnicalDocs } from '@/components/ui/TechnicalDocs';
import { RelatedProducts } from '@/components/ui/RelatedProducts';
import { PriceInquiryModal } from '@/components/ui/PriceInquiryModal';
import { Button } from '@/components/ui/Button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import type { ProductPagePayload } from '@/lib/get-product';
import { getCategorySlugFromName } from '@/lib/category-slugs';

type Props = {
  product: ProductPagePayload;
  children?: ReactNode;
};

export default function ProductPageClient({ product, children }: Props) {
  const [isInquiryModalOpen, setIsInquiryModalOpen] = useState(false);
  const [showStickyBar, setShowStickyBar] = useState(false);
  const [relatedProducts, setRelatedProducts] = useState<
    { id: string; name: string; slug: string; image: string; price: number; category: string }[]
  >([]);
  const [isRelatedProductsLoading, setIsRelatedProductsLoading] = useState(true);
  const productImages = Array.isArray(product.images) ? product.images.filter(Boolean) : [];
  const productFeatures = Array.isArray(product.features) ? product.features.filter(Boolean) : [];
  const productCategory = product.category || 'Diğer';

  useEffect(() => {
    const fetchRelatedProducts = async () => {
      try {
        setIsRelatedProductsLoading(true);
        const response = await fetch(
          `/api/products/related?productId=${product.id}&category=${encodeURIComponent(productCategory)}&limit=3`
        );
        const result = await response.json();

        if (result.success) {
          setRelatedProducts(result.data);
        } else {
          console.error('Benzer ürünler yüklenemedi:', result.error);
        }
      } catch (err) {
        console.error('Benzer ürünler yüklenirken hata:', err);
      } finally {
        setIsRelatedProductsLoading(false);
      }
    };

    fetchRelatedProducts();
  }, [product.id, productCategory]);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 350) {
        setShowStickyBar(true);
      } else {
        setShowStickyBar(false);
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const defaultSpecs = {
    dimensions: {
      width: 180,
      seatWidth: 160,
      height: 90,
      depth: 80,
      maxDepth: 160,
      diameter: 0,
      length: 0,
      weight: 0,
    },
    materials: {
      body: 'MDF',
      foot: 'Metal',
      armrest: 'MDF',
      frame: 'Ahşap',
      upholstery: 'Kumaş',
      finish: 'Mat',
      seat: 'Sünger',
      backrest: 'Sünger',
    },
  };

  const docs = {
    assembly: '/docs/assembly.pdf',
    packaging: '/docs/packaging.pdf',
    warranty: '/docs/warranty.pdf',
    maintenance: '/docs/maintenance.pdf',
    footModels: ['/images/foot-models/model1.webp', '/images/foot-models/model2.webp'],
  };

  const productType = productCategory.toLowerCase().includes('koltuk')
    ? 'sofa'
    : productCategory.toLowerCase().includes('masa')
      ? 'table'
      : productCategory.toLowerCase().includes('yatak')
        ? 'bed'
        : 'default';

  const dimensionsToShow = {
    sofa: ['width', 'seatWidth', 'height', 'depth', 'maxDepth', 'weight'],
    table: ['width', 'height', 'depth', 'length', 'weight'],
    bed: ['width', 'height', 'length', 'weight'],
    default: ['width', 'height', 'depth', 'weight'],
  };

  const materialsToShow = {
    sofa: ['body', 'upholstery', 'foot', 'seat', 'backrest'],
    table: ['body', 'finish'],
    bed: ['frame', 'upholstery', 'foot'],
    default: ['body', 'foot'],
  };

  const filteredDefaults = {
    dimensions: Object.fromEntries(
      Object.entries(defaultSpecs.dimensions).filter(([key]) =>
        dimensionsToShow[productType as keyof typeof dimensionsToShow].includes(key)
      )
    ),
    materials: Object.fromEntries(
      Object.entries(defaultSpecs.materials).filter(([key]) =>
        materialsToShow[productType as keyof typeof materialsToShow].includes(key)
      )
    ),
  };

  const productSpecs = {
    dimensions: { ...filteredDefaults.dimensions },
    materials: { ...filteredDefaults.materials },
  };

  const categorySlug = getCategorySlugFromName(productCategory) || 'diger';
  const breadcrumbItems = [
    { label: 'Ürünler', href: '/urunler' },
    ...(categorySlug !== 'diger'
      ? [{ label: productCategory, href: `/urunler/kategori/${categorySlug}` } as const]
      : []),
    { label: product.name, href: `/urunler/${categorySlug}/${product.slug}` },
  ];

  return (
    <div className="min-h-screen bg-white">
      <div className="bg-gradient-to-b from-gray-50/80 to-white py-8 lg:py-12">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="mb-6">
            <Breadcrumb items={breadcrumbItems} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16">
            <div className="lg:sticky lg:top-8 flex flex-col">
              <ProductGallery images={productImages} productName={product.name} />
            </div>

            <div className="flex flex-col justify-between space-y-8">
              <div className="space-y-8">
                <div className="space-y-4">
                  <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 tracking-tight">{product.name}</h1>
                  <p className="text-base lg:text-lg text-gray-600 leading-relaxed">{product.description}</p>
                </div>

                <div className="space-y-4">
                  <h3 className="text-xl font-semibold text-gray-900">Öne Çıkan Özellikler</h3>
                  <ul className="grid grid-cols-1 gap-3">
                    {productFeatures.slice(0, 4).map((feature, index) => (
                      <li key={index} className="flex items-start space-x-3 bg-gray-50/50 p1 rounded-lg">
                        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center mt-0.5">
                          <svg className="w-4 h-4 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </span>
                        <span className="text-gray-700 leading-tight">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <div className="bg-white rounded-xl border border-gray-200 p-6 lg:p-8 shadow-sm hover:shadow-md transition-shadow duration-300">
                <div className="text-center space-y-4">
                  <h3 className="text-xl font-semibold text-gray-900">Özel Fiyat Teklifi Alın</h3>
                  <p className="text-gray-600">
                    Bu ürünün fiyatı için lütfen bizimle iletişime geçin. Size özel fiyat teklifimizi sunalım.
                  </p>
                  <div className="grid grid-cols-2 gap-4">
                    <Button
                      onClick={() => setIsInquiryModalOpen(true)}
                      className="w-full bg-primary hover:bg-primary/90 text-white py-3 lg:py-4 text-lg font-medium rounded-lg transition-all duration-200 hover:shadow-lg active:scale-[0.98]"
                    >
                      Fiyat Teklifi Al
                    </Button>
                    <Button
                      onClick={() => {
                        const productUrl = `${window.location.origin}/urunler/${categorySlug}/${product.slug}`;
                        const text = `Merhaba, ${product.name} ürünü hakkında fiyat bilgisi almak istiyorum.\n\nÜrün sayfası: ${productUrl}`;
                        window.open(`https://wa.me/905323457227?text=${encodeURIComponent(text)}`, '_blank');
                      }}
                      className="w-full bg-[#25D366] hover:bg-[#25D366]/90 text-white py-3 lg:py-4 text-lg font-medium rounded-lg transition-all duration-200 hover:shadow-lg active:scale-[0.98] flex items-center justify-center gap-2"
                    >
                      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                      </svg>
                      WhatsApp
                    </Button>
                  </div>
                  <p className="text-xs text-gray-400 mt-4">Müşteri temsilcimiz en kısa sürede size dönüş yapacaktır.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12 lg:py-16 max-w-7xl">
        <Tabs defaultValue="features" className="space-y-8">
          <TabsList className="w-full border-b border-gray-200 flex space-x-8 lg:space-x-12">
            <TabsTrigger value="features" className="pb-4 text-base lg:text-lg font-medium">
              Tüm Özellikler
            </TabsTrigger>
            <TabsTrigger value="docs" className="pb-4 text-base lg:text-lg font-medium">
              Teknik Dökümanlar
            </TabsTrigger>
          </TabsList>

          <TabsContent value="features">
            <ProductInfo
              name={product.name}
              description={product.description}
              features={product.features}
              specs={productSpecs}
            />
          </TabsContent>

          <TabsContent value="docs">
            <TechnicalDocs docs={docs} productCategory={productCategory} />
          </TabsContent>
        </Tabs>
      </div>

      {children ? (
        <div className="container mx-auto px-4 max-w-7xl pt-4 lg:pt-10 pb-2 lg:pb-6 border-t border-gray-100">
          {children}
        </div>
      ) : null}

      <div className="bg-gray-50/70 py-12 lg:py-16">
        <div className="container mx-auto px-4 max-w-7xl">
          <h2 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-8 lg:mb-10 text-center">Benzer Ürünler</h2>

          {isRelatedProductsLoading ? (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
            </div>
          ) : relatedProducts.length > 0 ? (
            <RelatedProducts products={relatedProducts} currentProductId={product.id} />
          ) : (
            <div className="bg-white rounded-xl shadow-sm p-6 text-center">
              <p className="text-gray-500">Henüz benzer ürün bulunmuyor.</p>
            </div>
          )}
        </div>
      </div>

      <PriceInquiryModal
        isOpen={isInquiryModalOpen}
        onClose={() => setIsInquiryModalOpen(false)}
        productId={product.id}
        productName={product.name}
      />

      {/* Sticky Bottom Bar */}
      <div
        className={`fixed bottom-0 left-0 right-0 z-40 bg-white/70 dark:bg-gray-950/70 backdrop-blur-lg border-t border-gray-200/50 dark:border-gray-800/50 text-gray-900 dark:text-white shadow-2xl transition-all duration-300 transform ${
          showStickyBar ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0 pointer-events-none'
        }`}
      >
        <div className="container mx-auto px-4 py-3 max-w-7xl flex items-center justify-between gap-4">
          {/* Sol Kısım: Ürün Resmi ve Bilgileri */}
          <div className="flex items-center gap-3 min-w-0">
            {productImages[0] && (
              <div className="relative w-11 h-11 rounded-lg bg-gray-100 dark:bg-gray-800 border border-gray-200/60 dark:border-gray-700/60 overflow-hidden shrink-0 hidden sm:block">
                <img
                  src={productImages[0]}
                  alt={product.name}
                  className="w-full h-full object-cover"
                />
              </div>
            )}
            <div className="min-w-0">
              <span className="text-[10px] uppercase tracking-wider font-semibold text-primary block line-clamp-1">
                {productCategory}
              </span>
              <h4 className="text-sm font-bold text-gray-900 dark:text-white line-clamp-1">
                {product.name}
              </h4>
              {productFeatures.length > 0 && (
                <span className="text-[11px] text-gray-500 dark:text-gray-400 line-clamp-1 hidden md:block">
                  {productFeatures[0]}
                </span>
              )}
            </div>
          </div>

          {/* Sağ Kısım: Butonlar */}
          <div className="flex items-center gap-2 shrink-0">
            <Button
              onClick={() => setIsInquiryModalOpen(true)}
              className="bg-primary hover:bg-primary/95 text-white py-2.5 px-4 text-xs sm:text-sm font-semibold rounded-lg shadow-sm hover:shadow-md transition-all active:scale-[0.98] hidden sm:block"
            >
              Fiyat Teklifi Al
            </Button>
            <Button
              onClick={() => {
                const productUrl = `${window.location.origin}/urunler/${categorySlug}/${product.slug}`;
                const text = `Merhaba, ${product.name} ürünü hakkında fiyat bilgisi almak istiyorum.\n\nÜrün sayfası: ${productUrl}`;
                window.open(`https://wa.me/905323457227?text=${encodeURIComponent(text)}`, '_blank');
              }}
              className="bg-[#25D366] hover:bg-[#25D366]/90 text-white py-2.5 px-4 text-xs sm:text-sm font-bold rounded-lg shadow-sm hover:shadow-md transition-all flex items-center justify-center gap-1.5 active:scale-[0.98]"
            >
              <svg className="w-4 h-4 shrink-0" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
              </svg>
              <span>WhatsApp</span>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
